import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ContainerTab, ContainerTabs } from '@guildeducationinc/recess';
import { Box } from '@mui/material';
import { startCase } from 'lodash';
import { Messages, MessageType, TrackableButton, useAiSummary, useLocale, } from 'pe-core-frontend';
import { useState } from 'react';
import styles from './AiSummary.module.scss';
import { DocumentPresenceTable } from './DocumentPresenceTable';
import { ExtractionDisplay } from './ExtractionDisplay';
import { PromptCategory } from './typesAi';
import { ErrorDisplay, LoadingState, NoDataState, parseFilePathIntoFilename, transformAiSummaryResponse, } from './utils';
import { ValidationTable } from './ValidationTable';
export const AiSummaryList = ({ clientId, docs, documentEntity, documentEntityId, }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [isReprocessing, setIsReprocessing] = useState(false);
    const [messages, setMessages] = useState([]);
    const { t } = useLocale();
    const DocumentPresenceFieldsToDisplay = [
        'transcripts',
        'receipts',
        'tuitionBills',
        'syllabi',
        'proofOfCompletions',
        'proofOfEnrollments',
        'proofOfPayments',
        'genericDocuments',
    ];
    const ProgramOverviewFieldsToDisplay = [
        'program',
        'programLevel',
        'school',
        'fieldOfStudy',
        'startDate',
        'completionDate',
    ];
    const PaymentOverviewFieldsToDisplay = ['totalAmount', 'termStartDate', 'termEndDate'];
    const ExpenseFieldsToDisplay = ['courses', 'books', 'fees', 'materials', 'scholarships'];
    const { aiSummaryResponse, loading, error, triggerAiProcessor } = useAiSummary({
        clientId,
        documentEntity: documentEntity?.toString(),
        documentEntityId,
        promptCategory: PromptCategory.VALIDATION,
        returnMostRecent: true,
        pagination: {
            page: 1,
            pageSize: 10,
        },
    });
    const aiSummaryResponseItems = aiSummaryResponse
        ? transformAiSummaryResponse(aiSummaryResponse, PromptCategory.VALIDATION)
        : [];
    const { aiSummaryResponse: extractionData, loading: extractionLoading, error: extractionError, } = useAiSummary({
        clientId,
        docs: selectedDoc ? [selectedDoc] : docs,
        documentEntity: documentEntity?.toString(),
        documentEntityId,
        promptCategory: PromptCategory.EXTRACTION,
        returnMostRecent: true,
        pagination: {
            page: 1,
            pageSize: 10,
        },
    });
    const extractionDataItems = extractionData
        ? transformAiSummaryResponse(extractionData, PromptCategory.EXTRACTION)
        : [];
    const handleTabChange = (index) => {
        // Reset extraction data by changing selectedDoc
        setSelectedDoc(null);
        setActiveTab(index);
        // If it's a document tab (not the summary tab), set the new document
        if (index > 0 && docs && docs[index - 1]) {
            setSelectedDoc(docs[index - 1]);
        }
    };
    const handleReprocess = async () => {
        if (!docs || !documentEntityId) {
            return;
        }
        setIsReprocessing(true);
        setMessages([]);
        try {
            const success = await triggerAiProcessor({
                clientId,
                docs,
                documentEntity: documentEntity?.toString(),
                documentEntityId: parseInt(documentEntityId),
                promptCategory: PromptCategory.VALIDATION,
                processDocumentsTogether: false,
            });
            setIsReprocessing(false);
            if (success) {
                setMessages([
                    {
                        value: t('AI summary reprocessing has been submitted. Please refresh the page or check back in approximately 5 minutes to see the updated results.'),
                        type: MessageType.SUCCESS,
                        params: {},
                    },
                ]);
            }
            else {
                setMessages([
                    {
                        value: t('Error reprocessing AI summary. Please try again.'),
                        type: MessageType.ERROR,
                        params: {},
                    },
                ]);
            }
        }
        catch (err) {
            setIsReprocessing(false);
            setMessages([
                {
                    value: t('Error reprocessing AI summary. Please try again.'),
                    type: MessageType.ERROR,
                    params: {},
                },
            ]);
        }
    };
    if (loading) {
        return _jsx(LoadingState, {});
    }
    if (error) {
        return (_jsx(ErrorDisplay, { requestId: t('Error loading AI summary data'), error: t('Error loading AI summary data from the server') }));
    }
    const summary = aiSummaryResponseItems?.[0]; // Validation should only have one item
    const llmResponse = summary?.llmResponse;
    const informationPresence = llmResponse?.informationPresence;
    const documentPresence = llmResponse?.documentPresence;
    return (_jsxs(Box, { children: [_jsx(Messages, { messages: messages }), _jsx("div", { className: styles.container, children: _jsxs(ContainerTabs, { border: false, activeIndex: activeTab, onActive: handleTabChange, children: [_jsxs(ContainerTab, { title: t('Summary'), children: [_jsx("div", { className: styles.summaryHeader, children: _jsx(TrackableButton, { enText: 'Re-Process', tag: 'reprocess_validation', buttonSize: 'small', onClick: handleReprocess, disabled: isReprocessing || !docs || !documentEntityId, children: isReprocessing ? t('Re-Processing...') : t('Re-Process') }) }), _jsx("div", { className: styles.summaryContainer, children: !aiSummaryResponseItems?.length || !summary || !llmResponse ? (_jsx(NoDataState, { message: t('No AI Summary exists for this payment request.') })) : llmResponse.error ? (_jsx(ErrorDisplay, { requestId: summary.llmRequestId, error: llmResponse.error })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.requestId, children: [t('Request ID'), ": ", summary.llmRequestId] }), _jsx(ValidationTable, { title: t('Essential requirements'), fields: [
                                                    'program',
                                                    'programLevel',
                                                    'fieldOfStudy',
                                                    'totalAmount',
                                                    'scholarships',
                                                ], response: informationPresence, clientId: clientId, userId: summary.userId, llmRequestId: summary.llmRequestId, aiSummaryId: summary.id }), _jsx(ValidationTable, { title: t('Employee Name'), fields: ['employeeName'], response: informationPresence, clientId: clientId, userId: summary.userId, llmRequestId: summary.llmRequestId, aiSummaryId: summary.id }), _jsx(ValidationTable, { title: 'Program Overview', fields: ProgramOverviewFieldsToDisplay, response: informationPresence, clientId: clientId, userId: summary.userId, llmRequestId: summary.llmRequestId, aiSummaryId: summary.id }), _jsx(ValidationTable, { title: 'Payment Overview', fields: PaymentOverviewFieldsToDisplay, response: informationPresence, clientId: clientId, userId: summary.userId, llmRequestId: summary.llmRequestId, aiSummaryId: summary.id }), _jsx(ValidationTable, { title: 'Expenses', fields: ExpenseFieldsToDisplay, response: informationPresence, clientId: clientId, userId: summary.userId, llmRequestId: summary.llmRequestId, aiSummaryId: summary.id }), _jsx(DocumentPresenceTable, { title: 'Document Presence', fields: DocumentPresenceFieldsToDisplay, response: documentPresence, clientId: clientId, userId: summary.userId, llmRequestId: summary.llmRequestId, aiSummaryId: summary.id })] })) })] }, `summary-${summary?.id || 'empty'}`), docs?.map((doc, index) => {
                            const isCurrentDoc = doc === selectedDoc;
                            const docSummary = isCurrentDoc && extractionDataItems?.[0];
                            return (_jsx(ContainerTab, { title: parseFilePathIntoFilename(doc), children: _jsx("div", { className: styles.docContainer, children: extractionLoading ? (_jsx(LoadingState, {})) : extractionError ? (_jsx(ErrorDisplay, { requestId: t('Error loading AI extraction data'), error: t('Error loading AI extraction data from the server') })) : docSummary ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.requestId, children: [t('Request ID'), ": ", docSummary.llmRequestId] }), _jsxs("div", { className: styles.documentType, children: [t('Document Type'), ":", ' ', t(startCase(docSummary.documentType || 'Generic Document'))] }), _jsx(ExtractionDisplay, { llmRequestId: docSummary.llmRequestId, llmResponse: docSummary.llmResponse, documentType: docSummary.documentType || t('Generic Document'), clientId: clientId, userId: docSummary.userId, aiSummaryId: docSummary.id })] })) : (_jsx(NoDataState, { message: t('No extraction data is available for this document.') })) }) }, `doc-${doc}-${index}`));
                        })] }) })] }));
};
